import React, { useEffect, useState } from 'react';
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Lock, Unlock } from 'lucide-react';

// ============================================================================
// BoqGridHeader — AG Grid custom header matching the Team Overview table
// ============================================================================
// Renders the exact header treatment used on AdminTeamOverviewPage: a 10px
// uppercase grey label with a gold sort caret, plus per-column freeze (lock)
// and collapse (chevron) affordances. Behaviour is driven through the grid
// `context`, which the host page supplies:
//   context.collapsedColIds : Set<colId>     — columns shown as a sliver
//   context.lockedColIds    : Set<colId>     — columns pinned (frozen) left
//   context.staticColIds    : Set<colId>     — columns with no lock/collapse
//   context.toggleCollapse(colId) / toggleLock(colId)
// ============================================================================

const GOLD = '#9f902a';
const GREY = '#666666';

export default function BoqGridHeader(props) {
  const { displayName, column, enableSorting, progressSort, context } = props;
  const colId = column.getColId();
  const collapsed = context.collapsedColIds?.has(colId);
  const locked = context.lockedColIds?.has(colId);
  const isStatic = context.staticColIds?.has(colId);

  // Re-render the caret when this column's sort changes.
  const [sort, setSort] = useState(column.getSort());
  useEffect(() => {
    const onSortChanged = () => setSort(column.getSort());
    column.addEventListener('sortChanged', onSortChanged);
    return () => column.removeEventListener('sortChanged', onSortChanged);
  }, [column]);

  // Collapsed: just a chevron to expand again (matches Team Overview). The
  // chevron is always visible (gold) — it is the only way back out, so it must
  // never hide behind a hover.
  if (collapsed) {
    return (
      <div
        onClick={() => context.toggleCollapse(colId)}
        title={`Expand ${displayName}`}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', cursor: 'pointer' }}
      >
        <ChevronRight size={14} strokeWidth={2.5} style={{ color: GOLD }} />
      </div>
    );
  }

  return (
    <span className="boq-col-header" style={{ display: 'flex', alignItems: 'center', gap: 3, justifyContent: 'space-between', width: '100%' }}>
      <span
        onClick={enableSorting ? (e) => progressSort(e.shiftKey) : undefined}
        style={{
          cursor: enableSorting ? 'pointer' : 'default', display: 'inline-flex', alignItems: 'center', gap: 1, flex: 1, overflow: 'hidden',
          textTransform: 'uppercase', letterSpacing: '0.3px', fontSize: 10, fontWeight: 600, color: GREY, whiteSpace: 'nowrap',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{displayName}</span>
        {sort === 'asc' && <ChevronUp size={9} style={{ color: GOLD, flexShrink: 0 }} />}
        {sort === 'desc' && <ChevronDown size={9} style={{ color: GOLD, flexShrink: 0 }} />}
      </span>
      {/* Lock (gold = frozen) stays visible when active; the collapse chevron and
          the unlock toggle reveal on header hover so narrow column labels aren't
          crowded out (see qs-theme.css). */}
      {!isStatic && (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
          <span
            className={locked ? undefined : 'boq-col-header__tool'}
            onClick={(e) => { e.stopPropagation(); context.toggleLock(colId); }}
            title={locked ? 'Unfreeze column' : 'Freeze column'}
            style={{ cursor: 'pointer', display: locked ? 'inline-flex' : undefined, alignItems: 'center', padding: 1, color: locked ? GOLD : GREY, opacity: locked ? 1 : 0.4 }}
          >
            {locked ? <Lock size={10} /> : <Unlock size={10} />}
          </span>
          <span
            className="boq-col-header__tool"
            onClick={(e) => { e.stopPropagation(); context.toggleCollapse(colId); }}
            title="Collapse column"
            style={{ cursor: 'pointer', alignItems: 'center', padding: 1, color: GREY }}
          >
            <ChevronLeft size={11} />
          </span>
        </span>
      )}
    </span>
  );
}
